import React from "react";
import { useState, useContext } from "react";
import { Drawer } from "rsuite";
import { fetchCheckOut } from "../../../data/groupBooking/checkOutSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { fetchCruiseExtras } from "../../../data/groupBooking/getExtraSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import {
  Checkbox,
  FormControlLabel,
  Tooltip,
  useMediaQuery,
  Radio,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Fancybox from "../../../components/fancybox-library/Fancybox";
import { Fragment } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBedPulse,
  faExpand,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ViewMoreText from "./ViewMoreBtn";
import { BrandColorContext } from "../../../App";
import { useTranslation } from "react-i18next";

function RoomsSection({
  cruiseDetails,
  cruiseId,
  chainCode,
  durationDay,
  startDestId,
  endDestId,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const brandColor = useContext(BrandColorContext);
  const language = useSelector((state) => state.language.language) || "en";
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRateForPopup, setSelectedRateForPopup] = useState(null);
  const maxChars = 20;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // Add null check before accessing scrollHeight
    if (contentRef.current) {
      if (contentRef.current.scrollHeight > contentRef.current.clientHeight) {
        setShowButton(true);
      }
    }
  }, [cruiseDetails?.response]);
  const toggleText = () => {
    setIsExpanded((prev) => !prev);
  };
  const [selectedCabins, setSelectedCabins] = useState({});
  const [currentVisibleRoom, setCurrentVisibleRoom] = useState(0);

  const handleSelectCabin = (roomIndex, cabinId, cruiseRateID) => {
    setSelectedCabins((prev) => ({
      ...prev,
      [roomIndex]: { cabinId, cruiseRateID },
    }));
  };

  // Auto-scroll logic after selecting a room
  useEffect(() => {
    const totalRooms = cruiseDetails?.response?.cabinsChoices?.length || 0;
    const selectedCount = Object.keys(selectedCabins).length;

    if (selectedCount > 0 && selectedCount < totalRooms) {
      // Scroll to next room after a short delay
      setTimeout(() => {
        const nextRoomIndex = selectedCount;
        const nextRoomId = `SelectRoom_${nextRoomIndex + 1}`;
        const element = document.getElementById(nextRoomId);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }, 300);
    } else if (selectedCount === totalRooms && totalRooms > 0) {
      // All rooms selected, scroll to reserve button
      setTimeout(() => {
        const reserveButton = document.getElementById("reserve-button");
        if (reserveButton) {
          reserveButton.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 300);
    } else if (totalRooms === 1 && selectedCount === 1) {
      // Single room selected, scroll to reserve button
      setTimeout(() => {
        const reserveButton = document.getElementById("reserve-button");
        if (reserveButton) {
          reserveButton.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 300);
    }
  }, [selectedCabins, cruiseDetails?.response?.cabinsChoices?.length]);

  // Track current visible room for indicator
  useEffect(() => {
    const handleScroll = () => {
      const totalRooms = cruiseDetails?.response?.cabinsChoices?.length || 0;
      if (totalRooms === 0) return;

      const viewportMiddle = window.innerHeight * 0.4;
      let closestRoom = 0;
      let minDistance = Infinity;

      // Check which room is closest to viewport center
      for (let i = 1; i <= totalRooms; i++) {
        const element = document.getElementById(`SelectRoom_${i}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementMiddle = rect.top + rect.height / 2;
          const distance = Math.abs(elementMiddle - viewportMiddle);

          if (rect.top <= window.innerHeight && rect.bottom >= 0) {
            if (distance < minDistance) {
              minDistance = distance;
              closestRoom = i;
            }
          }
        }
      }

      // Check if reserve button is visible and closer
      const reserveButton = document.getElementById("reserve-button");
      if (reserveButton) {
        const rect = reserveButton.getBoundingClientRect();
        const elementMiddle = rect.top + rect.height / 2;
        const distance = Math.abs(elementMiddle - viewportMiddle);

        if (
          rect.top <= window.innerHeight &&
          rect.bottom >= 0 &&
          distance < minDistance
        ) {
          setCurrentVisibleRoom(totalRooms + 1);
          return;
        }
      }

      if (closestRoom > 0) {
        setCurrentVisibleRoom(closestRoom);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [cruiseDetails?.response?.cabinsChoices?.length]);
  const [drawerState, setDrawerState] = useState({
    open: false,
    selectedCabin: null,
  });

  // Update the handler to accept cabin data
  const handleOpenDrawer = (cabin) => {
    setDrawerState({
      open: true,
      selectedCabin: cabin,
    });
  };

  const handleCloseDrawer = () => {
    setDrawerState({
      open: false,
      selectedCabin: null,
    });
  };
  const handleReserveClick = useCallback(async () => {
    if (cruiseDetails) {
      const payload = {
        cruiseId: Number(cruiseId),
        itineraryId: Number(cruiseDetails.request.itineraryId),
        operationDate: cruiseDetails.request.operationDate,
        cabins: Object.keys(selectedCabins).map((roomIndex) => {
          const roomNumber = parseInt(roomIndex);
          const originalCabinRequest = cruiseDetails.request.cabins[roomNumber];
          return {
            cabinId: selectedCabins[roomIndex].cabinId,
            rateTypeId: selectedCabins[roomIndex].cruiseRateID,
            adults: originalCabinRequest.adults,
            children: originalCabinRequest.children,
            childrenAges: originalCabinRequest.childrenAges,
          };
        }),
      };

      const queryStringParams = {
        lang: language,
        chainCode,
        durationDay,
        startDestId,
        endDestId,
        cruiseId: payload.cruiseId,
        itineraryId: payload.itineraryId,
        operationDate: payload.operationDate,
        numCabins: payload.cabins.length,
      };
      payload.cabins.forEach((cabin, index) => {
        queryStringParams[`cabinId_room_${index + 1}`] = cabin.cabinId;
        queryStringParams[`rateTypeId_room_${index + 1}`] = cabin.rateTypeId;
        queryStringParams[`adults_room_${index + 1}`] = cabin.adults;
        queryStringParams[`children_room_${index + 1}`] = cabin.children;
        queryStringParams[`childAges_room_${index + 1}`] =
          cabin.childrenAges.join(",");
      });

      const queryString = new URLSearchParams({
        ...queryStringParams,
      }).toString();

      try {
        if (cruiseDetails.response?.isExtrasExists) {
          const resultAction = await dispatch(
            fetchCruiseExtras({ payload, chainCode, language })
          );
          if (fetchCruiseExtras.fulfilled.match(resultAction)) {
            navigate(`/add-extra?${queryString}`);
          } else {
            throw new Error(resultAction.error.message);
          }
        } else {
          const checkoutPayload = {
            booking: {
              cruiseId: payload.cruiseId,
              itineraryId: payload.itineraryId,
              operationDate: payload.operationDate,
            },
            cabins: payload.cabins,
            extras: [],
          };
          dispatch(
            fetchCheckOut({ payload: checkoutPayload, chainCode, language })
          );
          navigate(`/checkout?${queryString}`);
        }
      } catch (err) {
        alert(err.message || t("common.errorOccurredWhileSearching"));
      }
    }
  }, [cruiseDetails, dispatch, navigate, selectedCabins]);
  const totalRooms = cruiseDetails?.response?.cabinsChoices?.length || 0;
  const selectedCount = Object.keys(selectedCabins).length;
  const isSmallScreen = useMediaQuery("(max-width:1023px)");

  return (
    <section id="rooms" className="pt-30 pb-30">
      {/* Progress Indicator - Only show on desktop */}
      {totalRooms > 1 && !isSmallScreen && (
        <div
          style={{
            position: "fixed",
            right: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1000,
            background: "white",
            padding: "20px 12px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            minWidth: "80px",
            alignItems: "center",
          }}
        >
          {/* Title */}
          <div
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: brandColor,
              textAlign: "center",
              marginBottom: "5px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              lineHeight: "1.3",
            }}
          >
            {t("common.select")}
            <br />
            {t("common.rooms")}
          </div>
          <div
            style={{
              width: "100%",
              height: "1px",
              background: "#e0e0e0",
              margin: "5px 0",
            }}
          />
          {Array.from({ length: totalRooms }, (_, i) => i + 1).map(
            (roomNum) => (
              <div
                key={roomNum}
                onClick={() => {
                  const element = document.getElementById(
                    `SelectRoom_${roomNum}`
                  );
                  if (element) {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background:
                    currentVisibleRoom === roomNum
                      ? brandColor
                      : selectedCabins[roomNum - 1]
                        ? `${brandColor}30`
                        : "#f0f0f0",
                  color:
                    currentVisibleRoom === roomNum
                      ? "white"
                      : selectedCabins[roomNum - 1]
                        ? brandColor
                        : "#666",
                  border: `2px solid ${currentVisibleRoom === roomNum
                    ? brandColor
                    : selectedCabins[roomNum - 1]
                      ? brandColor
                      : "#ddd"
                    }`,
                  fontWeight: currentVisibleRoom === roomNum ? "600" : "500",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) => {
                  if (currentVisibleRoom !== roomNum) {
                    e.target.style.transform = "scale(1.1)";
                    e.target.style.background = `${brandColor}20`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentVisibleRoom !== roomNum) {
                    e.target.style.transform = "scale(1)";
                    e.target.style.background = selectedCabins[roomNum - 1]
                      ? `${brandColor}30`
                      : "#f0f0f0";
                  }
                }}
              >
                {selectedCabins[roomNum - 1] ? "✓" : roomNum}
              </div>
            )
          )}
          <div
            style={{
              width: "100%",
              height: "1px",
              background: "#e0e0e0",
              margin: "5px 0",
            }}
          />
          <div
            onClick={() => {
              const reserveButton = document.getElementById("reserve-button");
              if (reserveButton) {
                reserveButton.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              background:
                currentVisibleRoom === totalRooms + 1
                  ? brandColor
                  : selectedCount === totalRooms
                    ? `${brandColor}30`
                    : "#f0f0f0",
              color:
                currentVisibleRoom === totalRooms + 1
                  ? "white"
                  : selectedCount === totalRooms
                    ? brandColor
                    : "#666",
              border: `2px solid ${currentVisibleRoom === totalRooms + 1
                ? brandColor
                : selectedCount === totalRooms
                  ? brandColor
                  : "#ddd"
                }`,
              fontWeight: currentVisibleRoom === totalRooms + 1 ? "600" : "500",
              fontSize: "16px",
              marginTop: "5px",
            }}
            onMouseEnter={(e) => {
              if (currentVisibleRoom !== totalRooms + 1) {
                e.target.style.transform = "scale(1.1)";
                e.target.style.background = `${brandColor}20`;
              }
            }}
            onMouseLeave={(e) => {
              if (currentVisibleRoom !== totalRooms + 1) {
                e.target.style.transform = "scale(1)";
                e.target.style.background =
                  selectedCount === totalRooms ? `${brandColor}30` : "#f0f0f0";
              }
            }}
            title={t("common.reserve")}
          >
            {selectedCount === totalRooms ? "✓" : "→"}
          </div>
          <div
            style={{
              fontSize: "10px",
              fontWeight: "500",
              color: "#666",
              textAlign: "center",
              marginTop: "3px",
            }}
          >
            {t("common.reserve")}
          </div>
        </div>
      )}
      <div className="container">
        <div className="row pb-20">
          <div className="col-auto">
            <h3 className="text-22 fw-500">{t("common.staterooms")}</h3>
          </div>
        </div>

        {cruiseDetails?.response?.cabinsChoices?.length > 0 ? (
          cruiseDetails?.response?.cabinsChoices?.map((cabins, i) => (
            <div className="mt-20 mb-20" key={i}>
              {cruiseDetails?.response?.cabinsChoices?.length > 1 ? (
                <div>
                  {" "}
                  <h5
                    className="text-main text-center mb-3"
                    id={`SelectRoom_${i + 1}`}
                  >
                    {t("common.select")} {t("common.room")} {i + 1}{" "}
                  </h5>
                  {/* <p className=" text-center mb-2">
                  <small>
                    ({cabins.adultNumber} Adult
                    {cabins.adultNumber > 1 ? "s" : null}
                    {cabins.children > 0 ? `- ${cabins.children} Child` : ""})
                  </small>
                </p> */}
                </div>
              ) : null}
              {cabins.availableCabins?.length > 0 ? (
                cabins.availableCabins.map((cabin, idx) => (
                  <div
                    key={cabin.cabinID}
                    className="bg-blue-2 rounded-4 px-30 py-30 sm:px-20 sm:py-20 mb-3"
                  >
                    <div className="row y-gap-30 align-items-center">
                      <div className="col-xl-auto">
                        <div
                          className="ratio col-12 col-md-4 col-xl-12"
                          style={{ minWidth: "146px", minHeight: "146px" }}
                        >
                          <img
                            width={146}
                            height={146}
                            src={cabin.cabinMainPhoto}
                            alt={cabin.cabinNameSYS}
                            className="img-ratio rounded-4 object-cover"
                          />
                        </div>

                        <div className="">
                          <div className="text-18 fw-500 mt-10">
                            {cabin.cabinNameSYS}
                          </div>

                          <div className="y-gap-5 pt-5">
                            {cabin.mostPopularFeatures?.map((item, index) => (
                              <div className="d-flex items-center" key={index}>
                                <i className="icon-check text-10 mr-15"></i>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: item.featureIcon,
                                  }}
                                ></div>
                                {/* <img
                                                    width="20"
                                                    height="20"
                                                    className="mr-10"
                                                    src={item.cabinFeaturesItemName}
                                                    alt={item.cabinFeaturesItemName}
                                                  /> */}
                                <div className="text-15">{item.featureName}</div>
                              </div>
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleOpenDrawer(cabin)}
                            className="d-block text-15 fw-500 underline text-blue-1 mt-15"
                          >
                            {t("common.view")} {cabin.cabinNameSYS}{" "}
                          </button>
                        </div>
                      </div>
                      <div className="col-xl">
                        {cabin.rates.map((rate, index) => (
                          <div
                            className="bg-white rounded-4 px-30 py-30 my-3"
                            key={rate.rateTypeID}
                            style={{
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              border: `1px solid ${brandColor}20`,
                              transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = `0 4px 16px ${brandColor}30`;
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 2px 8px rgba(0,0,0,0.1)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <div className="row y-gap-30">
                              <div className="col-lg-3 col-md-6">
                                <div
                                  className="text-15 fw-500 mb-10"
                                  style={{ color: brandColor }}
                                >
                                  {rate.rateTypeName}
                                </div>
                                {/* Rate Desc  */}
                                {rate.rateDescription ? (
                                  <>
                                    <div
                                      className="text-12 fw-400 mb-10"
                                      style={{ lineHeight: "1.6" }}
                                    >
                                      {rate.rateDescription.length > maxChars
                                        ? rate.rateDescription.slice(0, maxChars) +
                                        "..."
                                        : rate.rateDescription}
                                      {rate.rateDescription.length > maxChars && (
                                        <button
                                          className="view-details-btn"
                                          onClick={() => {
                                            setSelectedRateForPopup(rate);
                                            setShowPopup(true);
                                          }}
                                          style={{
                                            marginTop: "8px",
                                            background: "transparent",
                                            border: "none",
                                            color: brandColor,
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            display: "block",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            transition: "all 0.3s ease",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.opacity = "0.8";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.opacity = "1";
                                          }}
                                        >
                                          {t("common.viewDetails")}
                                        </button>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  ""
                                )}
                                <div className="y-gap-5 pt-0">
                                  <ViewMoreText
                                    rate={rate}
                                    text={rate.c}
                                    initialLines={1}
                                  />
                                </div>
                              </div>

                              <div className="col-lg-3 col-6 border-left-light lg:border-none text-center">
                                <div className="px-40 lg:px-0">
                                  <div
                                    className="text-15 fw-500 mb-20"
                                    style={{ color: brandColor }}
                                  >
                                    {t("common.guests")}{" "}
                                  </div>

                                  <div className="d-flex justify-content-center g-5 align-items-end text-light-1 mb-1">
                                    {Array.from(
                                      {
                                        length:
                                          cruiseDetails?.request?.cabins[i]?.adults,
                                      },
                                      (_, index) => (
                                        <div
                                          key={index}
                                          className="icon-man text-30"
                                          style={{
                                            color: brandColor,
                                            opacity: 0.8,
                                          }}
                                        ></div>
                                      )
                                    )}

                                    {Array.from(
                                      {
                                        length:
                                          cruiseDetails?.request?.cabins[i]
                                            ?.children,
                                      },
                                      (_, index) => (
                                        <div
                                          key={index}
                                          className="icon-man text-20"
                                          style={{
                                            color: brandColor,
                                            opacity: 0.6,
                                          }}
                                        ></div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="col-lg-3 col-6 border-left-light lg:border-none">
                                <div className="mx-auto text-center d-flex flex-column justify-content-center align-items-center px-40 lg:px-0">
                                  <div
                                    className="text-15 fw-500 mb-20"
                                    style={{ color: brandColor }}
                                  >
                                    {t("common.bed")}
                                  </div>
                                </div>{" "}
                                <div className="mx-auto text-center">
                                  <i
                                    className="icon-bed text-15 m-1 pt-2"
                                    style={{ color: brandColor }}
                                  ></i>{" "}
                                  <span className="d-block text-center mt-1 text-10 text-secondary text-13 fw-500">
                                    {cabin.cabinBedType}
                                  </span>
                                </div>
                              </div>

                              <div className=" col-12 col-lg-3 mx-auto text-center border-left-light lg:border-none select-btn">
                                <div
                                  className="text-18 lh-14 fw-500 mb-15"
                                  style={{ color: brandColor }}
                                >
                                  {rate.rateCurrencyCode || "USD"}
                                  {rate.finalTotalCabinPrice}{" "}
                                  <span className="text-14 lh-14 text-light-1 mb-5 d-block">
                                    /{" "}
                                    {cruiseDetails?.response.itinerary?.numOfNights}{" "}
                                    {t("common.nights")}
                                  </span>
                                </div>

                                <Tooltip
                                  arrow
                                  title={
                                    i > 0 && !selectedCabins[i - 1]
                                      ? t("common.selectRoomFirst")
                                      : t("common.select")
                                  }
                                >
                                  <FormControlLabel
                                    value={rate.rateTypeID}
                                    control={
                                      <Radio
                                        icon={<CheckBoxOutlineBlankIcon />}
                                        disabled={i > 0 && !selectedCabins[i - 1]}
                                        checkedIcon={<CheckBoxIcon />}
                                        onChange={() =>
                                          handleSelectCabin(
                                            i,
                                            cabin.cabinID,
                                            rate.rateTypeID
                                          )
                                        }
                                        checked={
                                          selectedCabins[i]?.cruiseRateID ===
                                          rate.rateTypeID &&
                                          selectedCabins[i]?.cabinId ===
                                          cabin.cabinID
                                        }
                                        sx={{
                                          color: brandColor,
                                          "&.Mui-checked": {
                                            color: brandColor,
                                          },
                                          "&.Mui-disabled": {
                                            color: "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("common.select")}
                                    className="button h-50 px-15 border-blue-1 text-main mt-10 mx-auto"
                                    style={{
                                      borderColor: brandColor,
                                      color:
                                        selectedCabins[i]?.cruiseRateID ===
                                          rate.rateTypeID &&
                                          selectedCabins[i]?.cabinId === cabin.cabinID
                                          ? brandColor
                                          : "#333",
                                      background:
                                        selectedCabins[i]?.cruiseRateID ===
                                          rate.rateTypeID &&
                                          selectedCabins[i]?.cabinId === cabin.cabinID
                                          ? `${brandColor}10`
                                          : "transparent",
                                      transition: "all 0.3s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!(i > 0 && !selectedCabins[i - 1])) {
                                        e.currentTarget.style.borderColor =
                                          brandColor;
                                        e.currentTarget.style.background = `${brandColor}20`;
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (
                                        !(
                                          selectedCabins[i]?.cruiseRateID ===
                                          rate.rateTypeID &&
                                          selectedCabins[i]?.cabinId ===
                                          cabin.cabinID
                                        )
                                      ) {
                                        e.currentTarget.style.borderColor =
                                          brandColor;
                                        e.currentTarget.style.background =
                                          "transparent";
                                      }
                                    }}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-60 bg-blue-2 rounded-4 mt-20 mb-30">
                  <div className="row justify-center">
                    <div className="col-auto">
                      <i className="icon-search text-60 text-light-1 mb-20"></i>
                      <h4 className="text-22 fw-500">{t("common.noAvailableCabins")}</h4>
                      <p className="text-light-1 mt-10">
                        {t("common.tryChangingDates")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-60 bg-blue-2 rounded-4 mt-20 mb-50">
            <div className="row justify-center">
              <div className="col-auto">
                <i className="icon-search text-60 text-light-1 mb-20"></i>
                <h4 className="text-22 fw-500">{t("common.noAvailableCabins")}</h4>
                <p className="text-light-1 mt-10">
                  {t("common.tryChangingDates")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <Tooltip
        arrow
        left
        title={
          Object.keys(selectedCabins).length !==
            cruiseDetails?.response?.cabinsChoices?.length
            ? cruiseDetails?.response?.cabinsChoices?.length > 1
              ? t("common.selectCabinsFirst", {
                count: cruiseDetails?.response?.cabinsChoices?.length,
              })
              : t("common.selectCabinFirst")
            : t("common.reserveNow")
        }
      >
        <button
          id="reserve-button"
          type="button"
          onClick={handleReserveClick}
          disabled={
            Object.keys(selectedCabins).length !==
            cruiseDetails?.response?.cabinsChoices?.length
          }
          className="button h-50 px-15 -dark-1 bg-blue-1 text-white mt-10 mx-auto"
          style={{
            background: brandColor,
            border: "none",
            transition: "all 0.3s ease",
            opacity:
              Object.keys(selectedCabins).length !==
                cruiseDetails?.response?.cabinsChoices?.length
                ? 0.6
                : 1,
          }}
          onMouseEnter={(e) => {
            if (
              Object.keys(selectedCabins).length ===
              cruiseDetails?.response?.cabinsChoices?.length
            ) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = `0 4px 12px ${brandColor}40`;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "none";
          }}
        >
          {t("common.reserve")}{" "}
          <div className="icon-arrow-top-right ml-15"></div>
        </button>
      </Tooltip>
      {/* Single Popup for rate details - outside all maps */}
      {showPopup && selectedRateForPopup && (
        <div
          className="popup-overlay"
          onClick={() => {
            setShowPopup(false);
            setSelectedRateForPopup(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              padding: 0,
              borderRadius: "12px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "hidden",
              position: "relative",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                background: brandColor,
                color: "white",
                padding: "20px 30px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h6
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {selectedRateForPopup?.rateTypeName}
              </h6>
              <button
                onClick={() => {
                  setShowPopup(false);
                  setSelectedRateForPopup(null);
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                  e.target.style.transform = "rotate(90deg)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                  e.target.style.transform = "rotate(0deg)";
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="full-text"
              style={{
                padding: "25px 30px",
                maxHeight: "calc(80vh - 80px)",
                overflowY: "auto",
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
              }}
            >
              {selectedRateForPopup?.rateDescription}
            </div>
          </div>
        </div>
      )}
      {/* Single Drawer for all cabins - outside the map */}
      <Drawer
        size="full"
        placement="right"
        open={drawerState.open}
        onClose={handleCloseDrawer}
      >
        <Drawer.Header>
          <Drawer.Title>
            {drawerState?.selectedCabin?.cabinNameSYS} {t("common.details")}
          </Drawer.Title>
        </Drawer.Header>
        <Drawer.Body>
          <section className="pt-40">
            <div className="container">
              <div className="row y-gap-15 justify-between items-end">
                <div className="col-lg-10 mx-auto">
                  <h1 className="text-30 fw-600">
                    {drawerState?.selectedCabin?.cabinNameSYS}
                  </h1>

                  <div className="row x-gap-20 y-gap-20 items-center pt-10">
                    <div className="col-auto">
                      <div className="d-flex items-center">
                        <div className="text-14 text-light-1 ml-10">
                          <FontAwesomeIcon icon={faBedPulse} className="me-2" />
                          {drawerState?.selectedCabin?.cabinBedType} |{" "}
                          <FontAwesomeIcon icon={faExpand} className="me-2" />
                          {drawerState?.selectedCabin?.cabinSize}{" "}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="pt-40 js-pin-container">
            <div className="container">
              <div className="row y-gap-30">
                <div className="col-lg-10 mx-auto">
                  <div className="relative d-flex justify-center overflow-hidden gallery">
                    <Swiper
                      modules={[Navigation]}
                      slidesPerView={1}
                      navigation={{
                        prevEl: ".js-img-prev",
                        nextEl: ".js-img-next",
                      }}
                      className="swiper-wrapper"
                    >
                      {drawerState?.selectedCabin?.cabinPhotos?.length > 0 ? (
                        drawerState?.selectedCabin?.cabinPhotos?.map(
                          (image, index) => (
                            <SwiperSlide key={index}>
                              <img
                                src={image}
                                alt={drawerState?.selectedCabin?.cruiseName}
                                className="rounded-4 col-12 h-full object-cover"
                              />
                            </SwiperSlide>
                          )
                        )
                      ) : (
                        <SwiperSlide>
                          <div className="no-image-found d-flex justify-center  ">
                            <p className="text-danger">
                              {t("common.noImageFound")}
                            </p>
                          </div>
                        </SwiperSlide>
                      )}
                    </Swiper>
                    {drawerState?.selectedCabin?.cabinPhotos?.length > 1 ? (
                      <div className="absolute h-100 col-11">
                        <button className="section-slider-nav -prev flex-center button -blue-1 bg-white shadow-1 size-40 rounded-full sm:d-none js-img-prev">
                          <i className="icon icon-chevron-left text-12"></i>
                        </button>
                        <button className="section-slider-nav -next flex-center button -blue-1 bg-white shadow-1 size-40 rounded-full sm:d-none js-img-next">
                          <i className="icon icon-chevron-right text-12"></i>
                        </button>
                      </div>
                    ) : null}

                    <div className="absolute h-full col-12 z-2 px-20 py-20 d-flex justify-end items-end">
                      {drawerState?.selectedCabin?.cabinPhotos?.length > 0 ? (
                        <Fancybox>
                          {drawerState?.selectedCabin?.cabinPhotos?.length >
                            1 ? (
                            <a
                              href={drawerState?.selectedCabin?.cabinPhotos[0]}
                              className="button -blue-1 px-24 py-15 bg-white text-dark-1"
                              data-fancybox="cabin-gallery"
                              aria-label={`${t("common.galleryImage")} 1`}
                            >
                              {t("common.seeAllPhotos")}
                            </a>
                          ) : null}

                          {drawerState?.selectedCabin?.cabinPhotos
                            .slice(1)
                            ?.map((image, index) => (
                              <a
                                key={index}
                                href={image}
                                className="d-none"
                                data-fancybox="cabin-gallery"
                                aria-label={`${t("common.galleryImage")} ${index + 2
                                  }`}
                              ></a>
                            ))}
                        </Fancybox>
                      ) : null}
                    </div>
                  </div>

                  <div className="border-top-light mt-40 mb-40"></div>

                  <div className="row x-gap-40 y-gap-40">
                    <div className="col-12">
                      <h3 className="text-22 fw-500">
                        {t("common.overview")}{" "}
                      </h3>

                      {drawerState?.selectedCabin?.cabinDescription ? (
                        <Fragment>
                          <div
                            className={`text-dark-1 text-15 mt-20 ${isExpanded ? "expanded" : "collapsed"
                              }`}
                            ref={contentRef}
                            style={{
                              maxHeight: isExpanded ? "none" : "3em",
                              overflow: "hidden",
                              lineHeight: "1.5em",
                            }}
                            dangerouslySetInnerHTML={{
                              __html:
                                drawerState?.selectedCabin?.cabinDescription,
                            }}
                          />

                          {showButton && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                toggleText();
                              }}
                              className="d-block text-14 text-blue-1 fw-500 underline mt-10"
                            >
                              {isExpanded
                                ? t("common.showLess")
                                : t("common.viewMore")}
                            </button>
                          )}
                        </Fragment>
                      ) : (
                        <p className="text-danger">
                          {t("common.noDescriptionFound")}
                        </p>
                      )}
                    </div>
                    {drawerState?.selectedCabin?.cabinFeatures?.length > 0 ? (
                      <div className="row x-gap-40 y-gap-40">
                        <div className="col-12">
                          <h3 className="text-22 fw-500">
                            {t("common.facilities")}{" "}
                          </h3>

                          <div className="row x-gap-40 y-gap-40 pt-20">
                            {drawerState?.selectedCabin?.cabinFeatures?.map(
                              (item, index) => (
                                <div className="col-xl-4" key={item.index}>
                                  <div className="row y-gap-30">
                                    <div className="col-12">
                                      <div className="">
                                        <div className="d-flex items-center text-16 fw-500">
                                          <i className="icon-check text-10 mr-20"></i>
                                          {item.featureType}
                                        </div>

                                        <ul className="text-15 pt-10">
                                          {item.features?.map((subItem, i) => (
                                            <li
                                              className="d-flex items-center"
                                              key={i}
                                            >
                                              {" "}
                                              {subItem.featureName}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Drawer.Body>
      </Drawer>
    </section>
  );
}

export default RoomsSection;
